import React from 'react';

interface ContractorComplianceBannerProps {
  agreementRequiresAcceptance: boolean;
  accountReady: boolean;
  verificationStatusRaw: string | null;
  verificationState: string;
  rejectionReason: string | null;
  stripeConnected: boolean;
  stripePayoutReady: boolean;
  stripeAccountId?: string | null;
  loading: boolean;
  errorKind?: 'network' | 'auth' | 'not_found' | 'server' | null;
  adminView?: boolean;
  onNavigate: (screen: string) => void;
  onStartStripeOnboarding: () => void;
}

/**
 * Displays compliance/verification banners for contractors
 * - Agreement acceptance required
 * - Verification status (REJECTED, EXPIRED, SUBMITTED, NOT_STARTED, IN_PROGRESS)
 */
export default function ContractorComplianceBanner({
  agreementRequiresAcceptance,
  accountReady,
  verificationStatusRaw,
  verificationState,
  rejectionReason,
  stripeConnected,
  stripePayoutReady,
  stripeAccountId,
  loading,
  errorKind = null,
  adminView = false,
  onNavigate,
  onStartStripeOnboarding,
}: ContractorComplianceBannerProps) {
  // Don't show banners in admin view or while loading
  if (adminView || loading) {
    return null;
  }

  // If readiness data failed to load, don't falsely show "Complete verification"
  if (errorKind) {
    return (
      <div className="dashboard-status-pane" style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px 20px', marginBottom: '24px' }}>
        <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
          Unable to load account status. Refresh the page to try again.
        </p>
      </div>
    );
  }

  // Agreement acceptance banner (shown even if account is ready)
  if (agreementRequiresAcceptance && accountReady) {
    return (
      <div className="dashboard-status-pane" style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', padding: '16px 20px', marginBottom: '24px' }}>
        <p className="cb-banner-text" style={{ fontSize: '14px', color: '#9a3412', margin: '0 0 12px 0' }}>
          A new version of the Contractor Platform Agreement is available. Please review and accept it before submitting bids.
        </p>
        <button type="button" className="btn btn-primary" style={{ background: '#c2410c', borderColor: '#c2410c', color: '#fff' }} onClick={() => onNavigate('contractor-verification')}>
          Review &amp; Accept Agreement
        </button>
      </div>
    );
  }

  // Account not ready - show verification status banner with checklist (green check when step done)
  if (!accountReady) {
    const verificationDone = verificationState === 'approved';
    return (
      <div className="dashboard-status-pane" style={{
        background: verificationStatusRaw === 'REJECTED' || verificationStatusRaw === 'EXPIRED' ? '#fef2f2' : verificationStatusRaw === 'SUBMITTED' ? '#fef3c7' : '#fff7ed',
        border: `1px solid ${verificationStatusRaw === 'REJECTED' || verificationStatusRaw === 'EXPIRED' ? '#fecaca' : verificationStatusRaw === 'SUBMITTED' ? '#fde047' : '#fed7aa'}`,
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '24px'
      }}>
        <div className="cb-banner-content">
          {verificationStatusRaw === 'REJECTED' ? (
            <>
              <h2 className="cb-banner-title" style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 8px 0', color: '#dc2626' }}>
                Action required
              </h2>
              <p className="cb-banner-text" style={{ fontSize: '14px', color: '#991b1b', margin: '0 0 16px 0' }}>
                {rejectionReason || 'Your verification was rejected. Please address the feedback and resubmit.'}
              </p>
              <button type="button" className="btn btn-primary" onClick={() => onNavigate('contractor-verification')}>
                Update verification
              </button>
            </>
          ) : verificationStatusRaw === 'EXPIRED' ? (
            <>
              <h2 className="cb-banner-title" style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 8px 0', color: '#dc2626' }}>
                Verification expired
              </h2>
              <p className="cb-banner-text" style={{ fontSize: '14px', color: '#991b1b', margin: '0 0 16px 0' }}>
                Upload updated license/insurance documents to continue bidding.
              </p>
              <button type="button" className="btn btn-primary" onClick={() => onNavigate('contractor-verification')}>
                Update docs
              </button>
            </>
          ) : verificationState === 'pending' || verificationStatusRaw === 'SUBMITTED' ? (
            <>
              <h2 className="cb-banner-title" style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 8px 0', color: '#a16207' }}>
                Under review
              </h2>
              <p className="cb-banner-text" style={{ fontSize: '14px', color: '#92400e', margin: '0 0 16px 0' }}>
                Your verification has been submitted. We'll notify you once approved.
              </p>
              <ul className="cb-banner-checklist" style={{ listStyle: 'none', padding: 0, margin: '12px 0 16px 0' }}>
                <li style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#374151' }}>
                  <span style={{ fontSize: '16px' }}>{stripePayoutReady ? '✓' : stripeConnected ? '•' : '!'}</span>
                  <span>{stripePayoutReady ? 'Stripe connected — payouts active' : stripeConnected ? `Stripe connected — awaiting payout activation${stripeAccountId ? ` (${stripeAccountId})` : ''}` : 'Connect Stripe and complete payout setup'}</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#374151' }}>
                  <span style={{ fontSize: '14px', color: '#92400e', fontWeight: 700 }}>•</span>
                  <span>Verification pending admin approval</span>
                </li>
              </ul>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                <button
                  type="button"
                  disabled={stripeConnected}
                  onClick={stripeConnected ? undefined : onStartStripeOnboarding}
                  style={{
                    padding: '8px 16px', fontSize: '14px', fontWeight: 600, borderRadius: '6px',
                    border: `1px solid ${stripeConnected ? '#16a34a' : '#c2410c'}`,
                    background: stripeConnected ? '#16a34a' : '#c2410c',
                    color: '#fff', cursor: stripeConnected ? 'default' : 'pointer',
                  }}
                >
                  {stripeConnected ? 'Stripe connected' : 'Connect Stripe'}
                </button>
                <button
                  type="button"
                  disabled
                  style={{
                    padding: '8px 16px', fontSize: '14px', fontWeight: 600, borderRadius: '6px',
                    border: '1px solid #15803d', background: '#15803d',
                    color: '#fff', cursor: 'default',
                  }}
                >
                  Verification submitted
                </button>
              </div>
            </>
          ) : (
            <>
              <h2 className="cb-banner-title" style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 8px 0', color: '#c2410c' }}>
                {verificationStatusRaw === 'SUBMITTED' ? 'Verification Pending' : 'Complete setup to start bidding'}
              </h2>
              <p className="cb-banner-text" style={{ fontSize: '14px', color: '#9a3412', margin: '0 0 16px 0' }}>
                {verificationStatusRaw === 'SUBMITTED'
                  ? 'Your verification is under review. We\'ll notify you once approved.'
                  : 'Complete the steps below to bid on projects. After you submit for review, an admin will verify your profile.'}
              </p>
              <ul className="cb-banner-checklist" style={{ listStyle: 'none', padding: 0, margin: '0 0 16px 0' }}>
                <li style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '14px', color: '#374151' }}>
                  <span style={{ fontSize: '16px' }}>{stripePayoutReady ? '✓' : stripeConnected ? '•' : '!'}</span>
                  <span>{stripePayoutReady ? 'Stripe connected — payouts active' : stripeConnected ? `Stripe connected — awaiting payout activation${stripeAccountId ? ` (${stripeAccountId})` : ''}` : 'Connect Stripe and complete payout setup'}</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#374151' }}>
                  <span style={{ fontSize: '16px' }}>{verificationDone || verificationStatusRaw === 'SUBMITTED' ? '✓' : '!'}</span>
                  <span>
                    {verificationDone
                      ? 'Verification approved'
                      : verificationStatusRaw === 'SUBMITTED'
                        ? 'Waiting for approval'
                        : 'Submit license, insurance, W9, and accept the platform agreement for review'}
                  </span>
                </li>
              </ul>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {/* Stripe button — green + disabled once account is connected */}
                <button
                  type="button"
                  disabled={stripeConnected}
                  onClick={stripeConnected ? undefined : onStartStripeOnboarding}
                  style={{
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: 600,
                    borderRadius: '6px',
                    border: `1px solid ${stripeConnected ? '#16a34a' : '#c2410c'}`,
                    background: stripeConnected ? '#16a34a' : '#c2410c',
                    color: '#fff',
                    cursor: stripeConnected ? 'default' : 'pointer',
                    opacity: 1,
                  }}
                >
                  {stripeConnected ? 'Stripe connected' : 'Connect Stripe'}
                </button>
                {/* Verification button — always visible, green + disabled when approved or submitted */}
                {(() => {
                  const verifDone = verificationDone || verificationStatusRaw === 'SUBMITTED';
                  return (
                    <button
                      type="button"
                      disabled={verifDone}
                      onClick={verifDone ? undefined : () => onNavigate('contractor-verification')}
                      style={{
                        padding: '8px 16px',
                        fontSize: '14px',
                        fontWeight: 600,
                        borderRadius: '6px',
                        border: `1px solid ${verificationDone ? '#16a34a' : verificationStatusRaw === 'SUBMITTED' ? '#15803d' : '#c2410c'}`,
                        background: verificationDone ? '#16a34a' : verificationStatusRaw === 'SUBMITTED' ? '#15803d' : '#c2410c',
                        color: '#fff',
                        cursor: verifDone ? 'default' : 'pointer',
                        opacity: 1,
                      }}
                    >
                      {verificationDone ? 'Verification approved' : verificationStatusRaw === 'SUBMITTED' ? 'Verification submitted' : 'Contractor verification'}
                    </button>
                  );
                })()}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Account ready: show success banner with green checks for Stripe and verification
  return (
    <div className="dashboard-status-pane" style={{
      background: '#ecfdf5',
      border: '1px solid #a7f3d0',
      borderRadius: '8px',
      padding: '16px 20px',
      marginBottom: '24px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <h2 className="cb-banner-title" style={{ fontSize: '16px', fontWeight: '600', margin: 0, color: '#065f46' }}>
            You're all set to bid
          </h2>
          <p style={{ fontSize: '14px', color: '#047857', margin: '4px 0 0 0' }}>
            {stripeConnected && verificationState === 'approved'
              ? 'Stripe connected · Verification approved'
              : stripeConnected
                ? 'Stripe connected · Complete verification to unlock all features'
                : verificationState === 'approved'
                  ? 'Verification approved · Connect Stripe to receive payments'
                  : 'Ready to bid'}
          </p>
        </div>
      </div>
    </div>
  );
}
